"use client"

import { useState, useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { Play, Pause, Volume2 } from "lucide-react"

export interface AudioPlayerProps {
  title: string
  subtitle?: string    // e.g. "中文男声 · 科技感"
  duration: string     // e.g. "5:12"
  format?: string      // e.g. "MP3 320kbps"
  className?: string
}

// Generate a fake waveform array
function generateWaveform(count: number): number[] {
  const wave: number[] = []
  for (let i = 0; i < count; i++) {
    // Create a natural-looking waveform with some variation
    const base = 0.3 + Math.sin(i * 0.2) * 0.15
    const noise = (Math.random() - 0.5) * 0.4
    wave.push(Math.max(0.1, Math.min(1, base + noise)))
  }
  return wave
}

export function AudioPlayerCard({
  title,
  subtitle,
  duration,
  format,
  className,
}: AudioPlayerProps) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0) // 0-100
  const [waveform] = useState(() => generateWaveform(48))
  const timerRef = useRef<number | null>(null)

  // Parse duration to seconds
  const totalSeconds = (() => {
    const parts = duration.split(":").map(Number)
    if (parts.length === 2) return parts[0] * 60 + parts[1]
    return 0
  })()

  // Simulate playback
  useEffect(() => {
    if (playing) {
      timerRef.current = window.setInterval(() => {
        setProgress(p => {
          if (p >= 100) {
            setPlaying(false)
            return 0
          }
          return p + (100 / totalSeconds / 10) // update every 100ms
        })
      }, 100)
    } else if (timerRef.current) {
      clearInterval(timerRef.current)
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [playing, totalSeconds])

  const currentTime = (() => {
    const secs = Math.floor((progress / 100) * totalSeconds)
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${m}:${s.toString().padStart(2, "0")}`
  })()

  return (
    <div className={cn(
      "rounded-xl border border-border/60 bg-card/60 backdrop-blur-sm overflow-hidden",
      className,
    )}>
      <div className="px-4 py-3 flex items-center gap-3">
        {/* Play button */}
        <button
          onClick={() => setPlaying(!playing)}
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all duration-200",
            playing
              ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30 scale-105"
              : "bg-primary/10 text-primary hover:bg-primary/20"
          )}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>

        {/* Waveform + info */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <div className="flex items-center gap-2 mb-1.5">
            <Volume2 className="h-3 w-3 text-primary shrink-0" />
            <span className="text-sm font-medium text-foreground truncate">{title}</span>
            {format && (
              <span className="text-micro px-1.5 py-0.5 rounded-md bg-secondary text-muted-foreground font-medium shrink-0">
                {format}
              </span>
            )}
          </div>

          {/* Waveform bars */}
          <div className="flex items-end gap-[2px] h-8 mb-1">
            {waveform.map((h, i) => {
              const pct = (i / waveform.length) * 100
              const isActive = pct <= progress
              return (
                <div
                  key={i}
                  className={cn(
                    "flex-1 rounded-full transition-colors duration-150 min-w-[2px]",
                    isActive
                      ? "bg-primary"
                      : "bg-border/60 dark:bg-border/40"
                  )}
                  style={{ height: `${h * 100}%` }}
                />
              )
            })}
          </div>

          {/* Time + subtitle */}
          <div className="flex items-center justify-between text-caption text-muted-foreground">
            <span className="font-mono">{currentTime} / {duration}</span>
            {subtitle && <span className="truncate ml-2">{subtitle}</span>}
          </div>
        </div>
      </div>
    </div>
  )
}
